"use client";

import calculatePlayerLevel from "@/helpers/calculatePlayerLevel";
import useAnimatedXP from "@/hooks/useAnimatedXP";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "motion/react";
import { useLocale, useTranslations } from "next-intl";
import { Badge } from "../../badge";
import { useLevelUp } from "@/contexts/LevelUpContext";

export default function Level({
    session = null,
    user = null,
    integratedOnSidebar = false,
    onlyLevel = false,
}) {
    const locale = useLocale();
    const t = useTranslations("layout");
    const { triggerLevelUp } = useLevelUp();

    const [level, setLevel] = useState(0);
    const [xp, setXp] = useState(0);
    const [xpRequired, setXpRequired] = useState(0);
    const [xpForNextLevel, setXpForNextLevel] = useState(0);
    const [progressToNextLevel, setProgressToNextLevel] = useState(0);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [recentXpGain, setRecentXpGain] = useState(0); // Pour les gains d'XP
    const [recentXpLoss, setRecentXpLoss] = useState(0); // Pour les pertes d'XP
    const [showModal, setShowModal] = useState(false);
    // Niveau déjà célébré dans ce montage : couvre la fenêtre entre la pop-up
    // et le moment où le serveur renvoie le filigrane à jour.
    const celebratedLevelRef = useRef(0);

    const displayedXP = useAnimatedXP(xp);
    const displayedXPForNextLevel = useAnimatedXP(xpForNextLevel);
    const displayedLevel = useAnimatedXP(level);
    const displayedProgressToNextLevel = useAnimatedXP(progressToNextLevel);

    useEffect(() => {
        // Utiliser session.user en priorité, sinon user
        const fromSession = Boolean(session && session.user);
        const userData = fromSession ? session.user : user;

        if (!userData) {
            return;
        }

        // Session de repli (refresh-user en échec) ou révoquée : l'XP vient du
        // JWT figé au login et peut être très en retard. On n'en déduit rien —
        // ni fausse perte d'XP, ni pop-up de niveau — et une fois un affichage
        // sain posé, on le garde plutôt que de le faire reculer. Seule la toute
        // première peinture accepte ces valeurs : mieux vaut l'XP du login que
        // « Niveau 0 » pendant toute une panne du back.
        if (fromSession && (session.stale || session.revoked)) {
            if (isFirstLoad && typeof userData.xp === "number") {
                const fallback = calculatePlayerLevel(userData.xp);
                setLevel(fallback.level);
                setXp(userData.xp);
                setXpRequired(fallback.xpRequired);
                setXpForNextLevel(fallback.xpForNextLevel);
                setProgressToNextLevel(fallback.progressToNextLevel);
                // isFirstLoad reste true : la première session fraîche posera
                // les vraies valeurs sans flash de « gain » parasite.
            }
            return;
        }

        const { level, xpRequired, xpForNextLevel, progressToNextLevel } =
            calculatePlayerLevel(userData.xp);

        const xpDifference = userData.xp - xp; // Calculer la différence d'XP (gain ou perte)

        setLevel(level);
        setXp(userData.xp);
        setXpRequired(xpRequired);
        setXpForNextLevel(xpForNextLevel);
        setProgressToNextLevel(progressToNextLevel);

        if (xpDifference > 0 && !isFirstLoad) {
            // Gain d'XP
            setRecentXpGain(xpDifference);
            setRecentXpLoss(0); // Réinitialiser la perte si gain
            setTimeout(() => {
                setRecentXpGain(0); // Réinitialiser après 3 secondes
            }, 3000);
        } else if (xpDifference < 0 && !isFirstLoad) {
            // Perte d'XP
            setRecentXpLoss(Math.abs(xpDifference));
            setRecentXpGain(0); // Réinitialiser le gain si perte
            setTimeout(() => {
                setRecentXpLoss(0); // Réinitialiser après 3 secondes
            }, 3000);
        }

        // Célébration : uniquement depuis la session du visiteur (jamais le
        // profil d'un autre), en comparant au dernier niveau célébré persisté
        // côté serveur — la même référence pour tous les onglets et appareils.
        if (fromSession && typeof userData.lastCelebratedLevel === "number") {
            // Le localStorage couvre les fenêtres où le filigrane serveur est
            // en retard : autre onglet pendant que le POST est en vol, second
            // montage de Level (drawer + sidebar), rechargement après un POST
            // perdu. Clé par utilisateur : un même navigateur peut servir
            // plusieurs comptes.
            const storageKey = `blvmy_levelCelebrated_${userData.userId}`;
            let storedCelebrated = 0;
            try {
                storedCelebrated =
                    parseInt(localStorage.getItem(storageKey), 10) || 0;
            } catch (error) {
                // localStorage indisponible (navigation privée...) : le
                // filigrane serveur reste la seule référence.
            }

            const alreadyCelebrated = Math.max(
                userData.lastCelebratedLevel,
                celebratedLevelRef.current,
                storedCelebrated,
            );

            if (level > alreadyCelebrated) {
                celebratedLevelRef.current = level;
                try {
                    localStorage.setItem(storageKey, String(level));
                } catch (error) {
                    // Sans localStorage le verrou de ce montage suffit.
                }

                if (userData.levelUpPopinEnabled) {
                    // La carte de passage de niveau montre la barre repartie de
                    // zéro : on lui passe la progression du niveau tout juste
                    // entamé, pas celle qui vient d'être bouclée.
                    triggerLevelUp(level, progressToNextLevel);
                }

                // Pop-up désactivée comprise : le niveau est « vu », il ne
                // doit pas ressortir en célébration tardive si l'utilisateur
                // réactive la pop-up plus tard.
                fetch("/api/user/level-celebrated", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ level }),
                }).catch(() => {});
            }
        }

        setIsFirstLoad(false);
    }, [session, user, triggerLevelUp]);

    if (onlyLevel) {
        return (
            <span className="font-bold">{level.toLocaleString(locale)}</span>
        );
    }

    const modalContent = (
        <AnimatePresence>
            {showModal && (
                <div className="bg-[rgba(61,62,80,0.57)] dark:bg-bgDark-900/60 backdrop-blur-[12px] fixed top-0 left-0 bottom-0 right-0 flex justify-center lg:items-center z-[100000]">
                    <motion.div
                        variants={{
                            hidden: { opacity: 0, y: 15 },
                            visible: { opacity: 1, y: 0 },
                        }}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        className="flex lg:flex-row flex-col rounded-xl shadow lg:w-[900px] overflow-y-auto m-5 relative"
                    >
                            <div className="absolute top-5 right-5 z-10">
                                <svg
                                    onClick={() => setShowModal(false)}
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-8 h-8 text-blue-500 cursor-pointer"
                                    viewBox="0 0 24 24"
                                >
                                    <g fill="currentColor">
                                        <path d="M10.03 8.97a.75.75 0 0 0-1.06 1.06L10.94 12l-1.97 1.97a.75.75 0 1 0 1.06 1.06L12 13.06l1.97 1.97a.75.75 0 0 0 1.06-1.06L13.06 12l1.97-1.97a.75.75 0 1 0-1.06-1.06L12 10.94l-1.97-1.97Z"></path>
                                        <path
                                            fillRule="evenodd"
                                            d="M12 1.25C6.063 1.25 1.25 6.063 1.25 12S6.063 22.75 12 22.75S22.75 17.937 22.75 12S17.937 1.25 12 1.25ZM2.75 12a9.25 9.25 0 1 1 18.5 0a9.25 9.25 0 0 1-18.5 0Z"
                                            clipRule="evenodd"
                                        ></path>
                                    </g>
                                </svg>
                            </div>
                            <div className="bg-white dark:bg-bgDark-840 md:p-10 p-5 rounded-xl">
                                <h2 className="text-2xl font-bold font-sourceSans dark:text-white">
                                    {t("Level_modale_title")}
                                </h2>
                                <div className="mt-3 dark:text-lightBlue">
                                    <p>{t("Level_modale_description1")}</p>
                                    <p>{t("Level_modale_description2")}</p>
                                    <p>{t("Level_modale_description3")}</p>
                                </div>

                                <div className="bg-light dark:bg-bgDark-300 p-5 rounded-xl mt-5">
                                    <h3 className="text-xl dark:text-white font-bold font-sourceSans">
                                        {t("Level_modale_howToEarnXP_title")}
                                    </h3>
                                    <div className="mt-3 dark:text-lightBlue font-geistSans">
                                        {t(
                                            "Level_modale_howToEarnXP_description",
                                        )}
                                        <ul className="list-disc list-inside ml-8">
                                            <li>
                                                {t(
                                                    "Level_modale_howToEarnXP_1",
                                                )}
                                            </li>
                                            <li>
                                                {t(
                                                    "Level_modale_howToEarnXP_2",
                                                )}
                                            </li>
                                            <li>
                                                {t(
                                                    "Level_modale_howToEarnXP_3",
                                                )}
                                            </li>
                                            <li>
                                                {t(
                                                    "Level_modale_howToEarnXP_4",
                                                )}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );

    return (
        <>
            {/* About experience points */}
            {typeof document !== "undefined" &&
                createPortal(modalContent, document.body)}
            <div
                className="text-[#05060F]/40 dark:text-lightBlue text-xs cursor-pointer"
                onClick={() => setShowModal(true)}
            >
                {t("Level_level")} {displayedLevel.toLocaleString(locale)} (
                {displayedProgressToNextLevel}% -{" "}
                {displayedXP.toLocaleString(locale)}/
                {displayedXPForNextLevel.toLocaleString(locale)} {t("Level_xp")}
                )
                {recentXpGain > 0 && (
                    <span className="text-green-500">
                        {" "}
                        (+{recentXpGain.toLocaleString(locale)} {t("Level_xp")})
                    </span>
                )}
                {recentXpLoss > 0 && (
                    <span className="text-red-500">
                        {" "}
                        (-{recentXpLoss.toLocaleString(locale)} {t("Level_xp")})
                    </span>
                )}
            </div>
            <div className="h-2 bg-gray-200 dark:bg-bgDark-300 rounded-lg overflow-hidden mt-1">
                <div
                    className="h-full bg-primary"
                    style={{
                        width: `${displayedProgressToNextLevel}%`,
                    }}
                ></div>
            </div>
            {session &&
                session.user &&
                !session.user.ultimate &&
                session.user.pro && (
                    <div className="mt-1">
                        <Badge variant="premium" size="sm">
                            Pro
                        </Badge>
                    </div>
                )}
            {session && session.user && session.user.ultimate && (
                <div className="mt-1">
                    <Badge variant="ultra" size="sm">
                        Founder
                    </Badge>
                </div>
            )}
        </>
    );
}
